import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useToast } from "../components/ui/Toast";
import { t, formatMessage } from "./i18n";
import { invalidatePolicyViews } from "./policyQueries";

interface RecompileNoticeOptions {
  /** Optional explicit device count if the caller knows it. */
  deviceCount?: number;
  /** Short prefix shown in the toast title (e.g. action name). */
  title?: string;
  /** Optional secondary message (overrides default). */
  body?: string;
}

/**
 * Show an operator-facing toast after a Govern mutation that triggers a
 * backend recompile. Invalidates any policy/compile queries so dependent
 * panels (device detail Policy tab, dashboard) refetch the new version.
 */
export function useRecompileNotice() {
  const { push } = useToast();
  const qc = useQueryClient();

  return useCallback(
    (opts: RecompileNoticeOptions = {}) => {
      const title = opts.title ?? t("govern.recompile.savedTitle");
      const body =
        opts.body ??
        (opts.deviceCount !== undefined
          ? formatMessage("govern.recompile.savedBodyWithCount", {
              count: String(opts.deviceCount),
            })
          : t("govern.recompile.savedBody"));
      push({ tone: "success", title, body });
      void invalidatePolicyViews(qc);
    },
    [push, qc],
  );
}
